import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type AlertDetailResponse, type Alert, type Trade } from '@/lib/api';
import { ScoreBreakdown } from '@/components/ScoreBreakdown';
import { WalletStatsCard } from '@/components/WalletStats';
import {
    formatDate,
    formatMoney,
    formatWalletAddress,
    formatTimeToResolution,
    getSeverityEmoji,
    getSeverityLabel,
    getSeverityVariant,
} from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { ArrowLeft, ExternalLink } from 'lucide-react';

export function AlertDetail() {
    const { id } = useParams<{ id: string }>();
    const [alert, setAlert] = useState<Alert | null>(null);
    const [relatedTrades, setRelatedTrades] = useState<Trade[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            fetchAlert(id);
        }
    }, [id]);

    async function fetchAlert(alertId: string) {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get<AlertDetailResponse>(`/api/alerts/${alertId}`);

            if (response.success) {
                setAlert(response.data.alert);
                setRelatedTrades(response.data.relatedTrades);
            } else {
                setError('Alert not found');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load alert');
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !alert) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                    <span className="text-4xl mb-4">⚠️</span>
                    <p className="text-muted-foreground mb-4">{error || 'Alert not found'}</p>
                    <Button variant="secondary" asChild>
                        <Link to="/">Back to Alerts</Link>
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <Link
                    to="/"
                    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Alerts
                </Link>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                    {getSeverityEmoji(alert.severity)} {getSeverityLabel(alert.severity)}
                    <Badge variant={getSeverityVariant(alert.score)} className="font-mono text-base">
                        {alert.score}/100
                    </Badge>
                </h1>
                <p className="text-muted-foreground mt-1">{formatDate(alert.createdAt)}</p>
            </div>

            {/* Trade Info + Score Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Trade Details */}
                <Card>
                    <CardHeader>
                        <CardTitle>Trade Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg bg-muted/50">
                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                    Side
                                </div>
                                <div
                                    className={`text-xl font-bold ${alert.trade.side === 'YES' ? 'text-green-400' : 'text-red-400'
                                        }`}
                                >
                                    {alert.trade.side}
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50">
                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                    Price
                                </div>
                                <div className="text-xl font-bold font-mono">
                                    {(alert.trade.price * 100).toFixed(0)}¢
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50">
                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                    Size
                                </div>
                                <div className="text-xl font-bold font-mono">
                                    {formatMoney(alert.trade.size)}
                                </div>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/50">
                                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                                    Time to Resolve
                                </div>
                                <div className="text-xl font-bold">
                                    {formatTimeToResolution(alert.market.endDate)}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Score Breakdown */}
                <ScoreBreakdown
                    wallet={alert.scoreWallet}
                    size={alert.scoreSize}
                    timing={alert.scoreTiming}
                    priceEdge={alert.scorePriceEdge}
                    total={alert.score}
                />
            </div>

            {/* Market Info */}
            <Card>
                <CardHeader>
                    <CardTitle>Market</CardTitle>
                </CardHeader>
                <CardContent>
                    <h4 className="text-lg font-semibold mb-2">
                        {alert.market.question || alert.market.slug || alert.marketId}
                    </h4>
                    <div className="flex items-center gap-3">
                        {alert.market.category && (
                            <Badge variant="secondary" className="capitalize">
                                {alert.market.category}
                            </Badge>
                        )}
                        <Button variant="outline" size="sm" asChild>
                            <a
                                href={`https://polymarket.com/event/${alert.market.slug || alert.marketId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                View on Polymarket
                                <ExternalLink className="w-4 h-4 ml-2" />
                            </a>
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Wallet Info */}
            <div>
                <div className="flex items-center gap-4 mb-4">
                    <h3 className="text-lg font-semibold">Wallet</h3>
                    <Link
                        to={`/wallet/${alert.walletAddress}`}
                        className="font-mono text-muted-foreground hover:text-primary transition-colors"
                    >
                        {formatWalletAddress(alert.walletAddress)}
                    </Link>
                </div>
                <WalletStatsCard stats={alert.walletStats} classification={alert.wallet.classification} />
            </div>

            {/* Related Trades */}
            {relatedTrades.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Other Trades in This Market</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Time</TableHead>
                                    <TableHead>Side</TableHead>
                                    <TableHead>Price</TableHead>
                                    <TableHead className="text-right">Size</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {relatedTrades.map((trade) => (
                                    <TableRow key={trade.id}>
                                        <TableCell className="text-muted-foreground">
                                            {formatDate(trade.timestamp)}
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={`font-semibold ${trade.side === 'YES' ? 'text-green-400' : 'text-red-400'
                                                    }`}
                                            >
                                                {trade.side}
                                            </span>
                                        </TableCell>
                                        <TableCell className="font-mono">
                                            {(trade.price * 100).toFixed(0)}¢
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-medium">
                                            {formatMoney(trade.size)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
